import React from "react";
import { X, Check } from "lucide-react";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import { CompactButton, CompactPrimaryButton } from "../Button/Buttons";
import TextArea from "../Inputs/TextArea";
import DualViewEditor from "./DualViewEditor";

/**
 * FileEditorSection - Reusable file editor component with dirty-gated controls
 * 
 * Handles:
 * - Header showing a single Close button when clean, discard/commit when dirty
 * - Conversion warnings for agent type changes
 * - DualViewEditor for tools.json, policy.json, scopes.json (form + JSON views)
 * - Plain textarea for other file types
 * - Conversion context without changing the standard discard/commit controls
 * 
 * @param {Object} props
 * @param {string} props.fileType - 'tools', 'policy', 'scopes', or 'prompt'
 * @param {string} props.fileContent - Current file content
 * @param {string} props.originalFileContent - Original content for change detection
 * @param {boolean} props.isDirty - Optional parent-owned dirty state for multi-file transactions
 * @param {Function} props.onChange - Callback when content changes
 * @param {Function} props.onSave - Callback when the commit button is clicked
 * @param {Function} props.onCancel - Callback for both Close (clean) and discard (dirty)
 * @param {boolean} props.isSaving - Whether save is in progress
 * @param {string} props.saveLabel - Commit button label. Use "Apply" where onSave only
 *   updates a local draft and nothing is persisted until a separate page-level save.
 * @param {string} props.cancelLabel - Discard button label. Only used when there are changes.
 * @param {string} props.jsonError - JSON validation error message (if any)
 * @param {Object} props.fileChangeDetection - Change detection state from useFileChangeDetection
 * @param {Array} props.availableAgents - Available agents for swarm selection (tools only)
 * @param {string} props.currentAgentId - Current agent ID to filter from swarm selection (prevents self-reference)
 * @param {Function} props.onChangeDetected - Callback for change detection updates
 * @param {string} props.policyAtom - Read-only ATOM generated from policy.json
 */
const FileEditorSection = ({
  fileType,
  fileContent,
  originalFileContent,
  isDirty,
  onChange,
  onSave,
  onCancel,
  isSaving = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  jsonError = null,
  fileChangeDetection = {},
  availableAgents = [],
  currentAgentId = null,
  onChangeDetected,
  policyAtom = null,
  connectionManagedExternally = false
}) => {
  const supportsFormView = fileType === "tools" || fileType === "policy" || fileType === "scopes";

  // Detect if content has changed from original
  const hasChanges = isDirty ?? fileContent !== originalFileContent;

  // Check if we should show amber conversion button
  const shouldShowConversionButton = () => {
    if (fileType !== "tools" || !fileChangeDetection?.typeChanged) {
      return false;
    }

    try {
      const parsed = JSON.parse(fileContent);
      const hasAgents = parsed.agents && parsed.agents.length > 0;
      const hasTools = parsed.tools && parsed.tools.length > 0;
      return hasAgents || hasTools;
    } catch {
      return false;
    }
  };

  const showConversion = shouldShowConversionButton();

  // Determine if save button should be enabled
  const canSave = hasChanges && !jsonError && !isSaving;
  const disabledReason = jsonError ? `Fix validation errors to ${saveLabel.toLowerCase()}` : "";

  return (
    <div className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg p-4 bg-fill-gray-50 dark:bg-fill-deep-surface/50">
      {/* Header: Close when clean, discard/commit once edited */}
      <div className="flex items-center justify-end gap-2 mb-3">
        <CompactButton
          type="button" 
          onClick={onCancel} 
        >
          <X className="w-3.5 h-3.5" /> {hasChanges ? cancelLabel : "Close"}
        </CompactButton>
        
        {hasChanges && (
          <CompactPrimaryButton
            type="button" 
            onClick={onSave} 
            disabled={!canSave}
            title={disabledReason}
          >
            {isSaving ? (
              <InlineSpinner size="xs" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            {isSaving ? "Saving..." : saveLabel}
          </CompactPrimaryButton>
        )}
      </div>
      
      {/* Reserved space for conversion warning to prevent layout shift */}
      <div className="min-h-[28px] flex items-center justify-end mb-3">
        {showConversion && (
          <p className="text-xs text-ink-hex-d97706 dark:text-ink-amber-400">
            ⚠️ Converting from {fileChangeDetection?.changeInfo?.fromType === "expert" ? "Expert" : "Swarm"} to{" "}
            {fileChangeDetection?.changeInfo?.toType === "expert" ? "Expert" : "Swarm"} Agent
          </p>
        )}
      </div>
      
      {/* Content: DualViewEditor or Textarea */}
      {supportsFormView ? (
        <DualViewEditor
          value={fileContent}
          initialValue={originalFileContent}
          onChange={onChange}
          onError={(error) => {}} // Error handled by parent
          onChangeDetected={onChangeDetected}
          fileType={fileType}
          availableAgents={availableAgents}
          currentAgentId={currentAgentId}
          policyAtom={policyAtom}
          connectionManagedExternally={connectionManagedExternally}
        />
      ) : (
        <>
          {fileType === "prompt" && (
            <label className="block text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5">
              Agent Instructions
            </label>
          )}
          <TextArea
            value={fileContent}
            onChange={(e) => onChange(e.target.value)}
            rows={12}
            className={`font-mono resize-y ${jsonError ? "!border-edge-red-400" : ""}`}
          />
          {jsonError && <p className="text-xs text-ink-red-500 mt-2">{jsonError}</p>}
        </>
      )}
    </div>
  );
};

export default FileEditorSection;
